import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { useBookCall } from "../components/BookCallDialog";
import { useSeo, serviceSchema, breadcrumbSchema } from "../lib/seo";
import { Button } from "../components/ui/button";
import { ArrowUpRight, CheckCircle2, Wrench, Package, Truck, Sparkles } from "lucide-react";

const CONTENT = {
  en: {
    title: "Services — Furniture Assembly & Apartment Setup Munich | MUConcierge",
    description:
      "Professional furniture assembly service in Munich for expats: IKEA assembly, full apartment furniture setup, and white-glove move-in concierge. Fixed pricing, English-speaking team, insured, weekend slots.",
    tag: "Services",
    h1: "Furniture assembly & apartment setup services in Munich.",
    lead:
      "Three clear furniture assembly packages — built for English-speaking expats and international professionals who want their Munich apartment ready before they move in. Every plan includes professional furniture assembly, delivery coordination, packaging disposal and a final walkthrough.",
    ctaBook: "Book a free discovery call",
    backHome: "← Back to MUConcierge home",
    intro: {
      title: "What's included in every MUConcierge furniture assembly service in Munich",
      points: [
        "Professional furniture assembly team — IKEA, Home24, XXXLutz, BoConcept, Wayfair and more",
        "Delivery scheduling, receiving and signing on your behalf",
        "Room-by-room setup: bedroom, living room, home office",
        "Packaging removal, flattened cardboard and recycling per Munich rules",
        "Fully insured workers and property coverage",
        "English-first communication and email-only paperwork",
      ],
    },
    packages: [
      {
        code: "S",
        title: "IKEA Assembly",
        sub: "Dedicated IKEA furniture assembly service in Munich",
        desc: "From a single PAX wardrobe to a complete IKEA-furnished room — we are the professional IKEA furniture assembly experts in Munich. Whether you bought a flat-pack at IKEA München-Brunnthal, ordered IKEA home delivery, or assembled half a wardrobe and gave up, we finish the job properly.",
        bullets: [
          "PAX, PLATSA and BRIMNES wardrobes",
          "MALM, HEMNES and SONGESAND beds",
          "BEKANT, MICKE and ALEX desks",
          "BILLY bookcases, BESTÅ systems, METOD kitchen units",
          "Order pickup or delivery receiving",
          "Packaging removal and recycling",
        ],
        price: "Starting from €450",
        keywords: "IKEA assembly Munich · flat-pack assembly · wardrobe assembly · bed frame assembly · desk assembly",
        link: "/ikea-assembly-munich",
        linkLabel: "See full IKEA assembly page →",
        icon: Wrench,
      },
      {
        code: "M",
        title: "Full Apartment Setup",
        sub: "Most-booked move-in concierge package",
        desc: "We set up your entire Munich apartment so you can move in without disruption. Ideal for expats, couples, and 1–2 bedroom apartments. Includes multi-brand furniture assembly, multi-room planning and a same-day final walkthrough — the closest thing to a turnkey move-in service in Munich.",
        bullets: [
          "Multi-room planning and product list review",
          "Coordination with IKEA, Home24, XXXLutz and others",
          "Full assembly and furniture installation",
          "Room arrangement (bedroom, living room, workspace)",
          "Packaging removal and recycling",
          "Final walkthrough and handover",
        ],
        price: "Starting from €1,290",
        keywords: "apartment setup Munich · furniture installation · move-in service · expat relocation · flat-pack builders",
        icon: Package,
      },
      {
        code: "L",
        title: "White Glove Relocation Concierge",
        sub: "For families, executives & premium relocations",
        desc: "Complete done-for-you setup with priority coordination and ongoing post-move support. Designed for international families, senior executives and corporate HR partners who want a single accountable contact for the whole Munich move-in experience.",
        bullets: [
          "Pre-arrival planning and remote coordination",
          "Full apartment setup with premium scheduling",
          "Priority response within 1 business hour",
          "Custom shopping list and procurement",
          "Premium post-move support (returns, swaps, upgrades)",
          "Single invoice for corporate clients",
        ],
        price: "Custom quote",
        keywords: "Munich relocation concierge · executive moving · corporate relocation · white glove service · international moving Munich",
        icon: Sparkles,
      },
    ],
    addOns: {
      title: "Optional add-ons",
      items: [
        "48-hour rush apartment setup",
        "Furniture dismantling and disposal for the previous tenant or move-out",
        "Move-out cleaning coordination",
        "Home office setup before your first working day",
        "Returns and replacement coordination with retailers",
        "Storage transit and short-term holding for arriving freight",
      ],
    },
    process: {
      title: "How a MUConcierge booking works",
      steps: [
        { n: "01", t: "Discovery call", d: "Free 20-minute call to understand your apartment, timeline and product list." },
        { n: "02", t: "Quote & scope", d: "Fixed-price quote within 4 business hours. You approve in writing — no surprises." },
        { n: "03", t: "Coordination", d: "We schedule deliveries, receive freight, and align with your move-in date." },
        { n: "04", t: "Assembly day", d: "Two-person team assembles, installs and arranges every room — clean and on time." },
        { n: "05", t: "Walkthrough", d: "Final walkthrough, packaging removed, photos sent. Optional post-move support." },
      ],
    },
    finalCtaTitle: "Ready to book a furniture assembly service in Munich?",
    finalCtaSub: "Pick a package, request a quote, or book a free 20-minute discovery call.",
  },
  de: {
    title: "Leistungen — Möbelmontage & Wohnungseinrichtung München | MUConcierge",
    description:
      "Professionelle Möbelmontage in München für Expats: IKEA Aufbauservice, komplette Wohnungseinrichtung und White-Glove Move-In Concierge. Festpreise, englischsprachiges Team, versichert, Wochenendtermine.",
    tag: "Leistungen",
    h1: "Möbelmontage & Wohnungseinrichtung in München.",
    lead:
      "Drei klare Möbelmontage-Pakete — für englischsprachige Expats und internationale Fachkräfte, die ihre Münchner Wohnung einzugsfertig haben wollen. Jedes Paket umfasst professionelle Möbelmontage, Lieferkoordination, Verpackungsentsorgung und Abschluss-Begehung.",
    ctaBook: "Kostenloses Beratungsgespräch buchen",
    backHome: "← Zurück zur MUConcierge-Startseite",
    intro: {
      title: "In jedem MUConcierge Möbelmontage-Service in München inklusive",
      points: [
        "Professionelles Möbelmontage-Team — IKEA, Home24, XXXLutz, BoConcept, Wayfair u. a.",
        "Lieferplanung, Annahme und Quittung in Ihrem Namen",
        "Raum-für-Raum-Einrichtung: Schlafzimmer, Wohnzimmer, Homeoffice",
        "Verpackungsentsorgung, Kartonpressung und Recycling nach Münchner Regeln",
        "Voll sach- und personenhaftpflichtversichert",
        "Englischsprachige Kommunikation und papierlose Abwicklung",
      ],
    },
    packages: [
      {
        code: "S",
        title: "IKEA Möbelmontage",
        sub: "Dedizierter IKEA Aufbauservice in München",
        desc: "Vom einzelnen PAX-Kleiderschrank bis zum komplett IKEA-eingerichteten Zimmer — wir sind die IKEA Möbelmontage-Spezialisten in München. Egal ob Sie bei IKEA München-Brunnthal eingekauft, IKEA-Hauslieferung bestellt oder Halbgares hinterlassen haben — wir bauen sauber zu Ende auf.",
        bullets: [
          "PAX-, PLATSA- und BRIMNES-Kleiderschränke",
          "MALM-, HEMNES- und SONGESAND-Betten",
          "BEKANT-, MICKE- und ALEX-Schreibtische",
          "BILLY-Regale, BESTÅ-Systeme, METOD-Küchenfronten",
          "Abholung oder Lieferannahme",
          "Verpackungsentsorgung und Recycling",
        ],
        price: "Ab €450",
        keywords: "IKEA Möbelmontage München · Flat-Pack Aufbau · Schrankmontage · Bettmontage · Schreibtischmontage",
        link: "/ikea-moebelmontage-muenchen",
        linkLabel: "Zur IKEA-Möbelmontage-Seite →",
        icon: Wrench,
      },
      {
        code: "M",
        title: "Komplette Wohnungseinrichtung",
        sub: "Meistgebuchtes Move-In-Concierge-Paket",
        desc: "Wir richten Ihre gesamte Münchner Wohnung ein, damit Sie ohne Chaos einziehen können. Ideal für Expats, Paare und 1–2-Zimmer-Wohnungen. Inkl. markenübergreifender Möbelmontage, Mehrzimmerplanung und Abschluss-Begehung — der direkteste Weg zur einzugsfertigen Wohnung in München.",
        bullets: [
          "Mehrzimmer-Planung und Produktlisten-Review",
          "Koordination mit IKEA, Home24, XXXLutz u. a.",
          "Komplette Montage und Möbelinstallation",
          "Raumeinrichtung (Schlafzimmer, Wohnzimmer, Homeoffice)",
          "Verpackungsentsorgung und Recycling",
          "Abschluss-Begehung und Übergabe",
        ],
        price: "Ab €1.290",
        keywords: "Wohnungseinrichtung München · Möbelinstallation · Move-In Service · Expat-Relocation · Flat-Pack Aufbauer",
        icon: Package,
      },
      {
        code: "L",
        title: "White Glove Relocation Concierge",
        sub: "Für Familien, Führungskräfte & Premium-Relocations",
        desc: "Komplette done-for-you-Einrichtung mit Priorisierung und laufender Post-Move-Unterstützung. Für internationale Familien, Senior Executives und Corporate-HR-Partner, die einen einzigen Ansprechpartner für den gesamten Münchner Umzug wollen.",
        bullets: [
          "Pre-Arrival-Planung und Remote-Koordination",
          "Komplette Wohnungseinrichtung mit Premium-Scheduling",
          "Antwortzeit unter 1 Werktagsstunde",
          "Individuelle Shoppingliste und Beschaffung",
          "Premium-Post-Move-Support (Retouren, Tausch, Upgrades)",
          "Konsolidierte Rechnung für Corporate-Kunden",
        ],
        price: "Individuelles Angebot",
        keywords: "Münchner Relocation Concierge · Executive Umzug · Corporate Relocation · White-Glove-Service · internationaler Umzug München",
        icon: Sparkles,
      },
    ],
    addOns: {
      title: "Optionale Zusatzleistungen",
      items: [
        "48-Stunden-Express-Wohnungseinrichtung",
        "Möbeldemontage und Entsorgung für Vor- oder Nachmieter",
        "Auszugsreinigung-Koordination",
        "Homeoffice-Einrichtung vor dem ersten Arbeitstag",
        "Retouren- und Umtauschkoordination mit Händlern",
        "Transit-Lagerung und Kurzzeit-Storage für ankommendes Frachtgut",
      ],
    },
    process: {
      title: "So läuft eine MUConcierge-Buchung",
      steps: [
        { n: "01", t: "Beratungsgespräch", d: "Kostenloses 20-Minuten-Gespräch — wir verstehen Wohnung, Zeitplan und Produktliste." },
        { n: "02", t: "Angebot & Umfang", d: "Festpreis-Angebot innerhalb von 4 Werktagsstunden. Schriftliche Freigabe — keine Überraschungen." },
        { n: "03", t: "Koordination", d: "Wir planen Lieferungen, nehmen Fracht an und stimmen uns auf Ihren Einzugstermin ab." },
        { n: "04", t: "Montagetag", d: "Zwei-Personen-Team baut auf, installiert und richtet jeden Raum ein — sauber und pünktlich." },
        { n: "05", t: "Abschluss", d: "Begehung, Verpackung entsorgt, Fotos gesendet. Optionaler Post-Move-Support." },
      ],
    },
    finalCtaTitle: "Bereit, einen Möbelmontageservice in München zu buchen?",
    finalCtaSub: "Wählen Sie ein Paket, fordern Sie ein Angebot an oder buchen Sie ein kostenloses 20-Minuten-Beratungsgespräch.",
  },
};

export default function ServicesPage({ lang = "en" }) {
  const t = CONTENT[lang];
  const { openDialog } = useBookCall();
  const homePath = lang === "de" ? "/de" : "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://muconcierge.com";
  const path = lang === "de" ? "/de/leistungen" : "/services";

  useSeo({
    title: t.title,
    description: t.description,
    path,
    lang,
    alternates: { en: "/services", de: "/de/leistungen" },
    jsonLd: [
      ...t.packages.map((p) =>
        serviceSchema({
          name: p.title,
          description: p.desc,
          price: p.price.match(/\d[\d,.]*/)?.[0]?.replace(/[.,]/g, "") || undefined,
          url: origin + (p.link || path),
        })
      ),
      breadcrumbSchema([
        { name: lang === "de" ? "Startseite" : "Home", url: origin + homePath },
        { name: lang === "de" ? "Leistungen" : "Services", url: origin + path },
      ]),
    ],
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SiteHeader />

      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-20 md:pt-24 pb-12" data-testid="services-hero">
        <p className="tag-line text-[#C26D5C] mb-4">{t.tag}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A201C] tracking-tight leading-[1.05] max-w-4xl">
          {t.h1}
        </h1>
        <p className="mt-6 text-[#5C635A] leading-relaxed font-light text-lg max-w-3xl">{t.lead}</p>
        <div className="mt-8">
          <Button
            size="lg"
            onClick={() => openDialog({ source: `services-${lang}-top`, service: lang === "de" ? "Beratungsgespräch" : "Discovery call" })}
            className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6] px-7 py-5"
            data-testid="services-cta-top"
          >
            {t.ctaBook}
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="services-included">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-16">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1A201C] tracking-tight mb-8 max-w-3xl">{t.intro.title}</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {t.intro.points.map((p, i) => (
              <li key={i} className="flex items-start gap-3 bg-white border border-[#E5E2DC] p-5">
                <CheckCircle2 className="w-4 h-4 text-[#1C3326] mt-1 shrink-0" strokeWidth={1.5} />
                <span className="text-[#1A201C] text-sm leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-16 md:py-24" data-testid="services-packages">
        <div className="space-y-12">
          {t.packages.map((p, idx) => {
            const Icon = p.icon;
            return (
              <article
                key={p.code}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 border-b border-[#E5E2DC] pb-12 last:border-0"
                data-testid={`services-pkg-${p.code.toLowerCase()}`}
              >
                <div className="md:col-span-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#1C3326] text-[#FAF9F6] flex items-center justify-center font-serif text-lg">
                      {p.code}
                    </div>
                    <Icon className="w-5 h-5 text-[#C26D5C]" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#1A201C] tracking-tight leading-tight">{p.title}</h2>
                  <p className="mt-2 tag-line text-[#C26D5C]">{p.sub}</p>
                  <p className="mt-4 text-[#5C635A] leading-relaxed font-light">{p.desc}</p>
                  <p className="mt-6 font-serif text-2xl text-[#1A201C]">{p.price}</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button
                      onClick={() => openDialog({ source: `services-${lang}-pkg-${p.code}`, service: `${p.code} — ${p.title}` })}
                      className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
                      data-testid={`services-pkg-cta-${p.code.toLowerCase()}`}
                    >
                      {lang === "de" ? "Paket anfragen" : "Request this package"}
                      <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    {p.link && (
                      <Link
                        to={p.link}
                        className="inline-flex items-center text-sm text-[#1C3326] underline underline-offset-4 decoration-[#C26D5C]/40 hover:decoration-[#C26D5C]"
                        data-testid={`services-pkg-link-${p.code.toLowerCase()}`}
                      >
                        {p.linkLabel}
                      </Link>
                    )}
                  </div>
                </div>
                <div className="md:col-span-7">
                  <p className="tag-line text-[#1C3326] mb-3">{lang === "de" ? "Inklusive" : "Included"}</p>
                  <ul className="space-y-2">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-[#1A201C]">
                        <CheckCircle2 className="w-4 h-4 text-[#1C3326] mt-0.5 shrink-0" strokeWidth={1.5} />
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-xs text-[#5C635A] italic leading-relaxed">{p.keywords}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="services-addons">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-16">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1A201C] tracking-tight mb-8">{t.addOns.title}</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {t.addOns.items.map((it, i) => (
              <li key={i} className="bg-white border border-[#E5E2DC] p-5 flex items-start gap-3" data-testid={`services-addon-${i}`}>
                <Truck className="w-4 h-4 text-[#C26D5C] mt-1 shrink-0" strokeWidth={1.5} />
                <span className="text-[#1A201C] text-sm leading-relaxed">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-16 md:py-24" data-testid="services-process">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#1A201C] tracking-tight mb-10">{t.process.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[#E5E2DC] border border-[#E5E2DC]">
          {t.process.steps.map((s) => (
            <div key={s.n} className="bg-[#FAF9F6] p-6" data-testid={`services-step-${s.n}`}>
              <div className="font-serif text-3xl text-[#C26D5C] mb-3">{s.n}</div>
              <h3 className="font-serif text-lg text-[#1A201C] mb-2">{s.t}</h3>
              <p className="text-[#5C635A] text-sm leading-relaxed font-light">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#1C3326]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FAF9F6] tracking-tight leading-[1.1]">{t.finalCtaTitle}</h2>
          <p className="mt-4 text-[#FAF9F6]/80 max-w-xl mx-auto font-light">{t.finalCtaSub}</p>
          <Button
            size="lg"
            onClick={() => openDialog({ source: `services-${lang}-final`, service: lang === "de" ? "Beratungsgespräch" : "Discovery call" })}
            className="mt-8 rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] px-10 py-6 text-base"
            data-testid="services-cta-final"
          >
            {t.ctaBook}
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
          <div className="mt-6">
            <Link to={homePath} className="text-xs text-[#FAF9F6]/60 underline underline-offset-4 hover:text-[#E8C9B6]" data-testid="services-back-home">
              {t.backHome}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
